import React, { useEffect, useState } from 'react';
import { BRANDFETCH_CLIENT_ID } from '../../constants';

// Flagship brand domain per brand-alignment category (keyed by the category
// monogram used across the athlete profile + coach dashboard).
export const BRAND_DOMAINS = {
  NK: { domain: 'nike.com', brand: 'Nike' },
  GT: { domain: 'gatorade.com', brand: 'Gatorade' },
  UA: { domain: 'underarmour.com', brand: 'Under Armour' },
  FS: { domain: 'statefarm.com', brand: 'State Farm' },
  CT: { domain: 'att.com', brand: 'AT&T' },
  BT: { domain: 'beatsbydre.com', brand: 'Beats' },
};

// Drop shadow traces the logo's alpha silhouette, so even all-white marks
// stay visible on white tiles — one consistent treatment for every logo.
const LOGO_SHADOW = 'drop-shadow(0 0 1px rgba(0,0,0,0.30)) drop-shadow(0 2px 4px rgba(0,0,0,0.18))';

// The CDN answers 200 with a fully TRANSPARENT image for some brands (e.g.
// bodyarmor.com), so onError never fires and nothing paints. Probe the pixels
// (CDN allows CORS) and report whether the logo is actually visible.
const probeLogoVisible = (url, cb) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      if (img.naturalWidth < 8 || img.naturalHeight < 8) return cb(false);
      const s = 24;
      const c = document.createElement('canvas');
      c.width = s;
      c.height = s;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, s, s);
      const d = ctx.getImageData(0, 0, s, s).data;
      let opaque = 0;
      for (let i = 3; i < d.length; i += 4) {
        if (d[i] > 32) opaque += 1;
      }
      cb(opaque / (s * s) > 0.02);
    } catch (e) {
      cb(true); // can't inspect (tainted canvas): assume it's fine
    }
  };
  img.onerror = () => cb(false);
  img.src = url;
};

// Real brand logo from the Brandfetch CDN (client id is public by design).
// Missing or blank logos fall back to a soft branded monogram placeholder.
const BrandLogo = ({ domain, mono, size = 40, className = '' }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!domain) return undefined;
    let cancelled = false;
    probeLogoVisible(
      `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}`,
      (visible) => { if (!cancelled && !visible) setFailed(true); },
    );
    return () => { cancelled = true; };
  }, [domain]);

  if (!domain || failed) {
    return (
      <span
        className={`rounded-full bg-bm-orange/10 ring-1 ring-inset ring-bm-orange/20 flex items-center justify-center font-bold text-[#FF4400] shrink-0 ${className}`}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.3), letterSpacing: '0.5px' }}
        title="Logo unavailable"
      >
        {(mono || '?').slice(0, 2)}
      </span>
    );
  }

  return (
    <img
      src={`https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}`}
      alt={`${domain} logo by Brandfetch`}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`object-contain shrink-0 ${className}`}
      style={{ width: size, height: size, filter: LOGO_SHADOW }}
    />
  );
};

export default BrandLogo;
